import { Module } from 'vuex';
import { QuadroPersonalizadoService } from '@/services/QuadroPersonalizadoService';
import type {
  QuadroPersonalizadoInputType,
  QuadroPersonalizadoUpdateType,
  CampoPersonalizadoType,
  CampoPersonalizadoStoreType,
  QuadroPersonalizadoStoreType,
} from '@/types/quadroPersonalizado';

// Definindo o tipo localmente para evitar problemas de import circular
type QuadroPersonalizadoStateType = {
  quadroAtual: QuadroPersonalizadoStoreType | null;
  quadros: QuadroPersonalizadoStoreType[];
  campos: CampoPersonalizadoStoreType[];
  campoEdicao: CampoPersonalizadoStoreType | null;
  carregando: boolean;
  erro: string | null;
};

const quadroPersonalizadoService = new QuadroPersonalizadoService();

const state: QuadroPersonalizadoStateType = {
  quadroAtual: null,
  quadros: [],
  campos: [],
  campoEdicao: null,
  carregando: false,
  erro: null,
};

const getters = {
  quadroAtual: (state: QuadroPersonalizadoStateType) => state.quadroAtual,
  quadros: (state: QuadroPersonalizadoStateType) => state.quadros,
  campos: (state: QuadroPersonalizadoStateType) => state.campos,
  campoEdicao: (state: QuadroPersonalizadoStateType) => state.campoEdicao,
  camposOrdenados: (state: QuadroPersonalizadoStateType) => [...state.campos].sort((a, b) => a.ordem - b.ordem),
  camposAtivos: (state: QuadroPersonalizadoStateType) => state.campos.filter((campo) => campo.ativo),
  quantidadeCampos: (state: QuadroPersonalizadoStateType) => state.campos.length,
  carregando: (state: QuadroPersonalizadoStateType) => state.carregando,
  erro: (state: QuadroPersonalizadoStateType) => state.erro,
  temQuadroAtual: (state: QuadroPersonalizadoStateType) => !!state.quadroAtual,
  modoEdicao: (state: QuadroPersonalizadoStateType) => !!state.campoEdicao,
};

const mutations = {
  SET_QUADRO_ATUAL(state: QuadroPersonalizadoStateType, quadro: QuadroPersonalizadoStoreType | null) {
    state.quadroAtual = quadro;
    if (quadro) {
      state.campos = [...quadro.campos];
    } else {
      state.campos = [];
    }
  },
  SET_QUADROS(state: QuadroPersonalizadoStateType, quadros: QuadroPersonalizadoStoreType[]) {
    state.quadros = quadros;
  },
  ADICIONAR_QUADRO(state: QuadroPersonalizadoStateType, quadro: QuadroPersonalizadoStoreType) {
    state.quadros.push(quadro);
  },
  ATUALIZAR_QUADRO(state: QuadroPersonalizadoStateType, quadroAtualizado: QuadroPersonalizadoStoreType) {
    const index = state.quadros.findIndex((q) => q.id === quadroAtualizado.id);

    if (index !== -1) {
      state.quadros.splice(index, 1, quadroAtualizado);
    }

    if (state.quadroAtual?.id === quadroAtualizado.id) {
      state.quadroAtual = quadroAtualizado;
    }
  },
  REMOVER_QUADRO(state: QuadroPersonalizadoStateType, quadroId: string) {
    state.quadros = state.quadros.filter((q) => q.id !== quadroId);

    if (state.quadroAtual?.id === quadroId) {
      state.quadroAtual = null;
      state.campos = [];
    }
  },
  SET_CAMPOS(state: QuadroPersonalizadoStateType, campos: CampoPersonalizadoStoreType[]) {
    state.campos = campos;
  },
  ADICIONAR_CAMPO(state: QuadroPersonalizadoStateType, campo: CampoPersonalizadoStoreType) {
    state.campos.push(campo);

    if (state.quadroAtual) {
      state.quadroAtual.campos = [...state.campos];
      state.quadroAtual.dataAtualizacao = new Date();
    }
  },
  ATUALIZAR_CAMPO(state: QuadroPersonalizadoStateType, campoAtualizado: CampoPersonalizadoStoreType) {
    const index = state.campos.findIndex((c) => c.id === campoAtualizado.id);

    if (index !== -1) {
      state.campos.splice(index, 1, campoAtualizado);

      if (state.quadroAtual) {
        state.quadroAtual.campos = [...state.campos];
        state.quadroAtual.dataAtualizacao = new Date();
      }
    }
  },
  REMOVER_CAMPO(state: QuadroPersonalizadoStateType, campoId: string) {
    state.campos = state.campos.filter((c) => c.id !== campoId);

    if (state.quadroAtual) {
      state.quadroAtual.campos = [...state.campos];
      state.quadroAtual.dataAtualizacao = new Date();
    }
  },
  REORDENAR_CAMPOS(state: QuadroPersonalizadoStateType, novaOrdem: CampoPersonalizadoStoreType[]) {
    state.campos = novaOrdem.map((campo, index) => ({
      ...campo,
      ordem: index + 1,
    }));

    if (state.quadroAtual) {
      state.quadroAtual.campos = [...state.campos];
      state.quadroAtual.dataAtualizacao = new Date();
    }
  },
  SET_CARREGANDO(state: QuadroPersonalizadoStateType, carregando: boolean) {
    state.carregando = carregando;
  },
  SET_ERRO(state: QuadroPersonalizadoStateType, erro: string | null) {
    state.erro = erro;
  },
  LIMPAR_ERRO(state: QuadroPersonalizadoStateType) {
    state.erro = null;
  },
  SET_CAMPO_EDICAO(state: QuadroPersonalizadoStateType, campo: CampoPersonalizadoStoreType | null) {
    state.campoEdicao = campo;
  },
  LIMPAR_CAMPO_EDICAO(state: QuadroPersonalizadoStateType) {
    state.campoEdicao = null;
  },
  RESETAR_ESTADO(state: QuadroPersonalizadoStateType) {
    state.quadroAtual = null;
    state.campos = [];
    state.campoEdicao = null;
    state.erro = null;
    state.carregando = false;
  },
};

const actions = {
  async criarQuadro({ commit }: any, dadosQuadro: QuadroPersonalizadoInputType) {
    commit('SET_CARREGANDO', true);
    commit('LIMPAR_ERRO');

    try {
      const quadroCriado = await quadroPersonalizadoService.criar(dadosQuadro);

      const quadroLocal: QuadroPersonalizadoStoreType = {
        id: quadroCriado.id,
        nome: quadroCriado.nome,
        descricao: quadroCriado.descricao,
        ativo: quadroCriado.ativo,
        tipoConsultaId: quadroCriado.tipoConsultaId,
        tipoConsultaDescricao: quadroCriado.tipoConsultaDescricao,
        campos: (quadroCriado.estruturaCampos?.campos || []).map((campo: any) => ({
          id: campo.id,
          tipo: campo.tipo,
          nome: campo.nome,
          titulo: campo.titulo,
          ordem: campo.ordem,
          obrigatorio: campo.obrigatorio,
          ativo: campo.ativo,
          dataCriacao: campo.dataCriacao,
          valorMinimo: campo.valorMinimo,
          valorMaximo: campo.valorMaximo,
          casasDecimais: campo.casasDecimais !== undefined ? campo.casasDecimais : 0,
          unidade: campo.unidade !== undefined ? campo.unidade : 1,
          apenasPositivos: campo.apenasPositivos || false,
          tamanhoMinimo: campo.tamanhoMinimo,
          tamanhoMaximo: campo.tamanhoMaximo,
          multiplasLinhas: campo.multiplasLinhas || false,
          placeholder: campo.placeholder,
          dataMinima: campo.dataMinima,
          dataMaxima: campo.dataMaxima,
          formato: campo.formato || 'DD/MM/YYYY',
          opcoes: campo.opcoes || [],
          multiplaSelecao: campo.multiplaSelecao || false,
          valorPadrao: campo.valorPadrao || false,
          textoMarcado: campo.textoMarcado || 'Sim',
          textoDesmarcado: campo.textoDesmarcado || 'Não',
        })),
        quantidadeCampos: quadroCriado.quantidadeCampos || (quadroCriado.estruturaCampos?.campos?.length || 0),
        dataCriacao: quadroCriado.dataCriacao,
        dataAtualizacao: quadroCriado.dataAtualizacao || quadroCriado.dataCriacao,
      };

      commit('ADICIONAR_QUADRO', quadroLocal);
      commit('SET_QUADRO_ATUAL', quadroLocal);
      return quadroLocal;
    } catch (error: any) {
      const mensagemErro = error.response?.data?.message || error.message || 'Erro ao criar quadro';
      commit('SET_ERRO', mensagemErro);
      throw new Error(mensagemErro);
    } finally {
      commit('SET_CARREGANDO', false);
    }
  },
  async carregarQuadros({ commit }: any) {
    commit('SET_CARREGANDO', true);
    commit('LIMPAR_ERRO');

    try {
      const quadrosApi = await quadroPersonalizadoService.obterTodos();

      let dadosQuadros = [];
      if (Array.isArray(quadrosApi)) {
        dadosQuadros = quadrosApi;
      } else if (quadrosApi && (quadrosApi as any).data && Array.isArray((quadrosApi as any).data)) {
        dadosQuadros = (quadrosApi as any).data;
      } else {
        dadosQuadros = [];
      }

      const quadros: QuadroPersonalizadoStoreType[] = dadosQuadros.map((q) => ({
        id: q.id,
        nome: q.nome,
        descricao: q.descricao,
        ativo: q.ativo,
        tipoConsultaId: q.tipoConsultaId,
        tipoConsultaDescricao: q.tipoConsultaDescricao,
        campos: (q.estruturaCampos?.campos || []).map((campo: any) => ({
          id: campo.id,
          tipo: campo.tipo,
          nome: campo.nome,
          titulo: campo.titulo,
          ordem: campo.ordem,
          obrigatorio: campo.obrigatorio,
          ativo: campo.ativo,
          dataCriacao: campo.dataCriacao,
          valorMinimo: campo.valorMinimo,
          valorMaximo: campo.valorMaximo,
          casasDecimais: campo.casasDecimais !== undefined ? campo.casasDecimais : 0,
          unidade: campo.unidade !== undefined ? campo.unidade : 1,
          apenasPositivos: campo.apenasPositivos || false,
          tamanhoMinimo: campo.tamanhoMinimo,
          tamanhoMaximo: campo.tamanhoMaximo,
          multiplasLinhas: campo.multiplasLinhas || false,
          placeholder: campo.placeholder,
          dataMinima: campo.dataMinima,
          dataMaxima: campo.dataMaxima,
          formato: campo.formato || 'DD/MM/YYYY',
          opcoes: campo.opcoes || [],
          multiplaSelecao: campo.multiplaSelecao || false,
          valorPadrao: campo.valorPadrao || false,
          textoMarcado: campo.textoMarcado || 'Sim',
          textoDesmarcado: campo.textoDesmarcado || 'Não',
        })),
        quantidadeCampos: q.quantidadeCampos || (q.estruturaCampos?.campos?.length || 0),
        dataCriacao: q.dataCriacao,
        dataAtualizacao: q.dataAtualizacao || q.dataCriacao,
      }));

      commit('SET_QUADROS', quadros);
      return quadros;
    } catch (error: any) {
      const mensagemErro = error.response?.data?.message || error.message || 'Erro ao carregar quadros';
      commit('SET_ERRO', mensagemErro);
      throw new Error(mensagemErro);
    } finally {
      commit('SET_CARREGANDO', false);
    }
  },
  async carregarQuadroPorId({ commit }: any, quadroId: string) {
    commit('SET_CARREGANDO', true);
    commit('LIMPAR_ERRO');

    try {
      const quadroApi = await quadroPersonalizadoService.obterPorId(quadroId);

      const quadroLocal: QuadroPersonalizadoStoreType = {
        id: quadroApi.id,
        nome: quadroApi.nome,
        descricao: quadroApi.descricao,
        ativo: quadroApi.ativo,
        tipoConsultaId: quadroApi.tipoConsultaId,
        tipoConsultaDescricao: quadroApi.tipoConsultaDescricao,
        campos: (quadroApi.estruturaCampos?.campos || []).map((campo: any) => ({
          id: campo.id,
          tipo: campo.tipo,
          nome: campo.nome,
          titulo: campo.titulo,
          ordem: campo.ordem,
          obrigatorio: campo.obrigatorio,
          ativo: campo.ativo,
          dataCriacao: campo.dataCriacao,
          valorMinimo: campo.valorMinimo,
          valorMaximo: campo.valorMaximo,
          casasDecimais: campo.casasDecimais !== undefined ? campo.casasDecimais : 0,
          unidade: campo.unidade !== undefined ? campo.unidade : 1,
          apenasPositivos: campo.apenasPositivos || false,
          tamanhoMinimo: campo.tamanhoMinimo,
          tamanhoMaximo: campo.tamanhoMaximo,
          multiplasLinhas: campo.multiplasLinhas || false,
          placeholder: campo.placeholder,
          dataMinima: campo.dataMinima,
          dataMaxima: campo.dataMaxima,
          formato: campo.formato || 'DD/MM/YYYY',
          opcoes: campo.opcoes || [],
          multiplaSelecao: campo.multiplaSelecao || false,
          valorPadrao: campo.valorPadrao || false,
          textoMarcado: campo.textoMarcado || 'Sim',
          textoDesmarcado: campo.textoDesmarcado || 'Não',
        })),
        quantidadeCampos: quadroApi.quantidadeCampos || (quadroApi.estruturaCampos?.campos?.length || 0),
        dataCriacao: quadroApi.dataCriacao,
        dataAtualizacao: quadroApi.dataAtualizacao || quadroApi.dataCriacao,
      };

      commit('SET_QUADRO_ATUAL', quadroLocal);
      return quadroLocal;
    } catch (error: any) {
      const mensagemErro = error.response?.data?.message || error.message || 'Erro ao carregar quadro';
      commit('SET_ERRO', mensagemErro);
      throw new Error(mensagemErro);
    } finally {
      commit('SET_CARREGANDO', false);
    }
  },
  async atualizarQuadro({ commit }: any, dadosQuadro: QuadroPersonalizadoUpdateType) {
    commit('SET_CARREGANDO', true);
    commit('LIMPAR_ERRO');

    try {
      const quadroAtualizado = await quadroPersonalizadoService.atualizar(
        dadosQuadro.id,
        dadosQuadro,
      );

      const quadroLocal: QuadroPersonalizadoStoreType = {
        id: quadroAtualizado.id,
        nome: quadroAtualizado.nome,
        descricao: quadroAtualizado.descricao,
        ativo: quadroAtualizado.ativo,
        tipoConsultaId: quadroAtualizado.tipoConsultaId,
        tipoConsultaDescricao: quadroAtualizado.tipoConsultaDescricao,
        campos: (quadroAtualizado.estruturaCampos?.campos || []).map((campo: any) => ({
          id: campo.id,
          tipo: campo.tipo,
          nome: campo.nome,
          titulo: campo.titulo,
          ordem: campo.ordem,
          obrigatorio: campo.obrigatorio,
          ativo: campo.ativo,
          dataCriacao: campo.dataCriacao,
          valorMinimo: campo.valorMinimo,
          valorMaximo: campo.valorMaximo,
          casasDecimais: campo.casasDecimais !== undefined ? campo.casasDecimais : 0,
          unidade: campo.unidade !== undefined ? campo.unidade : 1,
          apenasPositivos: campo.apenasPositivos || false,
          tamanhoMinimo: campo.tamanhoMinimo,
          tamanhoMaximo: campo.tamanhoMaximo,
          multiplasLinhas: campo.multiplasLinhas || false,
          placeholder: campo.placeholder,
          dataMinima: campo.dataMinima,
          dataMaxima: campo.dataMaxima,
          formato: campo.formato || 'DD/MM/YYYY',
          opcoes: campo.opcoes || [],
          multiplaSelecao: campo.multiplaSelecao || false,
          valorPadrao: campo.valorPadrao || false,
          textoMarcado: campo.textoMarcado || 'Sim',
          textoDesmarcado: campo.textoDesmarcado || 'Não',
        })),
        quantidadeCampos: quadroAtualizado.quantidadeCampos || (quadroAtualizado.estruturaCampos?.campos?.length || 0),
        dataCriacao: quadroAtualizado.dataCriacao,
        dataAtualizacao: quadroAtualizado.dataAtualizacao || quadroAtualizado.dataCriacao,
      };

      commit('ATUALIZAR_QUADRO', quadroLocal);
      return quadroLocal;
    } catch (error: any) {
      const mensagemErro = error.response?.data?.message || error.message || 'Erro ao atualizar quadro';
      commit('SET_ERRO', mensagemErro);
      throw new Error(mensagemErro);
    } finally {
      commit('SET_CARREGANDO', false);
    }
  },
  async excluirQuadro({ commit }: any, quadroId: string) {
    commit('SET_CARREGANDO', true);
    commit('LIMPAR_ERRO');

    try {
      await quadroPersonalizadoService.excluir(quadroId);
      commit('REMOVER_QUADRO', quadroId);
      return true;
    } catch (error: any) {
      const mensagemErro = error.response?.data?.message || error.message || 'Erro ao excluir quadro';
      commit('SET_ERRO', mensagemErro);
      throw new Error(mensagemErro);
    } finally {
      commit('SET_CARREGANDO', false);
    }
  },
  async buscarQuadrosPorNome({ commit }: any, nome: string) {
    commit('SET_CARREGANDO', true);
    commit('LIMPAR_ERRO');

    try {
      const quadrosApi = await quadroPersonalizadoService.buscarPorNome(nome);

      const quadros: QuadroPersonalizadoStoreType[] = quadrosApi.map((q) => ({
        id: q.id,
        nome: q.nome,
        descricao: q.descricao,
        ativo: q.ativo,
        campos: (q.estruturaCampos?.campos || []).map((campo: any) => ({
          id: campo.id,
          tipo: campo.tipo,
          nome: campo.nome,
          titulo: campo.titulo,
          ordem: campo.ordem,
          obrigatorio: campo.obrigatorio,
          ativo: campo.ativo,
          dataCriacao: campo.dataCriacao,
          valorMinimo: campo.valorMinimo,
          valorMaximo: campo.valorMaximo,
          casasDecimais: campo.casasDecimais !== undefined ? campo.casasDecimais : 0,
          unidade: campo.unidade !== undefined ? campo.unidade : 1,
          apenasPositivos: campo.apenasPositivos || false,
          tamanhoMinimo: campo.tamanhoMinimo,
          tamanhoMaximo: campo.tamanhoMaximo,
          multiplasLinhas: campo.multiplasLinhas || false,
          placeholder: campo.placeholder,
          dataMinima: campo.dataMinima,
          dataMaxima: campo.dataMaxima,
          formato: campo.formato || 'DD/MM/YYYY',
          opcoes: campo.opcoes || [],
          multiplaSelecao: campo.multiplaSelecao || false,
          valorPadrao: campo.valorPadrao || false,
          textoMarcado: campo.textoMarcado || 'Sim',
          textoDesmarcado: campo.textoDesmarcado || 'Não',
        })),
        quantidadeCampos: q.quantidadeCampos || (q.estruturaCampos?.campos?.length || 0),
        dataCriacao: q.dataCriacao,
        dataAtualizacao: q.dataAtualizacao || q.dataCriacao,
      }));

      commit('SET_QUADROS', quadros);
      return quadros;
    } catch (error: any) {
      const mensagemErro = error.response?.data?.message || error.message || 'Erro ao buscar quadros';
      commit('SET_ERRO', mensagemErro);
      throw new Error(mensagemErro);
    } finally {
      commit('SET_CARREGANDO', false);
    }
  },
  selecionarQuadro({ commit }: any, quadro: QuadroPersonalizadoStoreType) {
    commit('SET_QUADRO_ATUAL', quadro);
  },
  limparQuadroAtual({ commit }: any) {
    commit('SET_QUADRO_ATUAL', null);
  },
  async adicionarCampo({ commit, state }: any, dadosCampo: any) {
    commit('LIMPAR_ERRO');

    try {
      const novoCampo: CampoPersonalizadoStoreType = {
        id: `campo_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        tipo: dadosCampo.tipo,
        nome: dadosCampo.nome || `Campo ${dadosCampo.tipo}`,
        titulo: dadosCampo.titulo || dadosCampo.nome || `Campo ${dadosCampo.tipo}`,
        ordem: state.campos.length + 1,
        obrigatorio: dadosCampo.obrigatorio || false,
        ativo: true,
        dataCriacao: new Date(),

        // Propriedades específicas para campos numéricos
        valorMinimo: dadosCampo.valorMinimo || null,
        valorMaximo: dadosCampo.valorMaximo || null,
        casasDecimais: dadosCampo.casasDecimais !== undefined ? dadosCampo.casasDecimais : 0,
        unidade: dadosCampo.unidade !== undefined ? dadosCampo.unidade : 1,
        apenasPositivos: dadosCampo.apenasPositivos || false,

        // Propriedades específicas para campos de texto
        tamanhoMinimo: dadosCampo.tamanhoMinimo || null,
        tamanhoMaximo: dadosCampo.tamanhoMaximo || null,
        multiplasLinhas: dadosCampo.multiplasLinhas || false,
        placeholder: dadosCampo.placeholder || null,

        // Propriedades específicas para campos de data
        dataMinima: dadosCampo.dataMinima || null,
        dataMaxima: dadosCampo.dataMaxima || null,
        formato: dadosCampo.formato || 'DD/MM/YYYY',

        // Propriedades específicas para campos de seleção
        opcoes: dadosCampo.opcoes || [],
        multiplaSelecao: dadosCampo.multiplaSelecao || false,

        // Propriedades específicas para campos checkbox
        valorPadrao: dadosCampo.valorPadrao || false,
        textoMarcado: dadosCampo.textoMarcado || 'Sim',
        textoDesmarcado: dadosCampo.textoDesmarcado || 'Não',
      };

      commit('ADICIONAR_CAMPO', novoCampo);
      return novoCampo;
    } catch (error) {
      commit('SET_ERRO', `Erro ao adicionar campo: ${error}`);
      throw error;
    }
  },
  editarCampo(
    { commit }: any,
    {
      campoId,
      dadosAtualizados,
    }: { campoId: string; dadosAtualizados: Partial<CampoPersonalizadoStoreType> },
  ) {
    commit('LIMPAR_ERRO');

    try {
      const campoAtualizado: CampoPersonalizadoStoreType = {
        ...dadosAtualizados,
        id: campoId,
      } as CampoPersonalizadoStoreType;

      commit('ATUALIZAR_CAMPO', campoAtualizado);
      return campoAtualizado;
    } catch (error) {
      commit('SET_ERRO', `Erro ao editar campo: ${error}`);
      throw error;
    }
  },
  removerCampo({ commit }: any, campoId: string) {
    commit('LIMPAR_ERRO');

    try {
      commit('REMOVER_CAMPO', campoId);
    } catch (error) {
      commit('SET_ERRO', `Erro ao remover campo: ${error}`);
      throw error;
    }
  },
  reordenarCampos({ commit }: any, novaOrdem: CampoPersonalizadoStoreType[]) {
    commit('LIMPAR_ERRO');

    try {
      commit('REORDENAR_CAMPOS', novaOrdem);
    } catch (error) {
      commit('SET_ERRO', `Erro ao reordenar campos: ${error}`);
      throw error;
    }
  },
  limparErro({ commit }: any) {
    commit('LIMPAR_ERRO');
  },
  resetarEstadoCompleto({ commit }: any) {
    commit('RESETAR_ESTADO');
  },
  iniciarEdicaoCampo({ commit }: any, campo: CampoPersonalizadoStoreType) {
    commit('SET_CAMPO_EDICAO', campo);
  },
  cancelarEdicaoCampo({ commit }: any) {
    commit('LIMPAR_CAMPO_EDICAO');
  },
  finalizarEdicaoCampo({ commit }: any) {
    commit('LIMPAR_CAMPO_EDICAO');
  },
  async salvarDadosQuadro(
    { commit, getters }: any,
    {
      quadroId,
      entidadeId,
      tipoEntidade,
      dados,
      atendimentoId,
    }: {
      quadroId: string;
      entidadeId: string;
      tipoEntidade: string;
      dados: { [key: string]: any };
      atendimentoId?: number;
    },
  ) {
    commit('SET_CARREGANDO', true);
    commit('LIMPAR_ERRO');

    try {
      let campos: CampoPersonalizadoType[] = [];
      const { quadroAtual } = getters;

      if (quadroAtual && quadroAtual.id === quadroId) {
        campos = quadroAtual.estruturaCampos?.campos || [];
      } else {
        const quadro = await quadroPersonalizadoService.obterPorId(quadroId);
        campos = quadro.estruturaCampos?.campos || [];
      }

      const dadosSalvos = await quadroPersonalizadoService.salvarDadoCompatibilidade(
        quadroId,
        entidadeId,
        tipoEntidade,
        dados,
        campos,
        atendimentoId,
      );
      return dadosSalvos;
    } catch (error: any) {
      const mensagemErro = error.response?.data?.message || error.message || 'Erro ao salvar dados do quadro';
      commit('SET_ERRO', mensagemErro);
      throw new Error(mensagemErro);
    } finally {
      commit('SET_CARREGANDO', false);
    }
  },
  async carregarDadosQuadro(
    { commit }: any,
    {
      quadroId,
      entidadeId,
      tipoEntidade,
    }: {
      quadroId: string;
      entidadeId: string;
      tipoEntidade: string;
    },
  ) {
    commit('SET_CARREGANDO', true);
    commit('LIMPAR_ERRO');

    try {
      const dados = await quadroPersonalizadoService.obterDadoPorEntidade(
        quadroId,
        entidadeId,
        tipoEntidade,
      );
      return dados;
    } catch (error: any) {
      const mensagemErro = error.response?.data?.message || error.message || 'Erro ao carregar dados do quadro';
      commit('SET_ERRO', mensagemErro);
      throw new Error(mensagemErro);
    } finally {
      commit('SET_CARREGANDO', false);
    }
  },
  async carregarTodosDadosEntidade(
    { commit }: any,
    {
      entidadeId,
      tipoEntidade,
    }: {
      entidadeId: string;
      tipoEntidade: string;
    },
  ) {
    commit('SET_CARREGANDO', true);
    commit('LIMPAR_ERRO');

    try {
      const dadosEntidade = await quadroPersonalizadoService.obterDadosPorEntidade(
        entidadeId,
        tipoEntidade,
      );
      return dadosEntidade;
    } catch (error: any) {
      const mensagemErro = error.response?.data?.message || error.message || 'Erro ao carregar dados da entidade';
      commit('SET_ERRO', mensagemErro);
      throw new Error(mensagemErro);
    } finally {
      commit('SET_CARREGANDO', false);
    }
  },
  async atualizarDadosQuadro(
    { commit, getters }: any,
    {
      dadoId,
      dados,
      quadroId,
      atendimentoId,
    }: {
      dadoId: string;
      dados: { [key: string]: any };
      quadroId?: string;
      atendimentoId?: number;
    },
  ) {
    commit('SET_CARREGANDO', true);
    commit('LIMPAR_ERRO');

    try {
      let campos: CampoPersonalizadoType[] = [];
      const { quadroAtual } = getters;

      if (quadroAtual && (!quadroId || quadroAtual.id === quadroId)) {
        campos = quadroAtual.estruturaCampos?.campos || [];
      } else if (quadroId) {
        const quadro = await quadroPersonalizadoService.obterPorId(quadroId);
        campos = quadro.estruturaCampos?.campos || [];
      }

      const dadosAtualizados = await quadroPersonalizadoService.atualizarDadoCompatibilidade(
        dadoId,
        dados,
        campos,
        atendimentoId,
      );
      return dadosAtualizados;
    } catch (error: any) {
      const mensagemErro = error.response?.data?.message || error.message || 'Erro ao atualizar dados do quadro';
      commit('SET_ERRO', mensagemErro);
      throw new Error(mensagemErro);
    } finally {
      commit('SET_CARREGANDO', false);
    }
  },
  async excluirDadosQuadro({ commit }: any, dadoId: string) {
    commit('SET_CARREGANDO', true);
    commit('LIMPAR_ERRO');

    try {
      await quadroPersonalizadoService.excluirDado(dadoId);
      return true;
    } catch (error: any) {
      const mensagemErro = error.response?.data?.message || error.message || 'Erro ao excluir dados do quadro';
      commit('SET_ERRO', mensagemErro);
      throw new Error(mensagemErro);
    } finally {
      commit('SET_CARREGANDO', false);
    }
  },
  async verificarNomeExiste(
    { commit }: any,
    { nome, idExcluir }: { nome: string; idExcluir?: string },
  ) {
    commit('LIMPAR_ERRO');

    try {
      const existe = await quadroPersonalizadoService.verificarNomeExiste(nome, idExcluir);
      return existe;
    } catch (error: any) {
      const mensagemErro = error.response?.data?.message || error.message || 'Erro ao verificar nome do quadro';
      commit('SET_ERRO', mensagemErro);
      throw new Error(mensagemErro);
    }
  },
};

const quadroPersonalizadoStore: Module<QuadroPersonalizadoStateType, any> = {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};

export default quadroPersonalizadoStore;
export { QuadroPersonalizadoStateType };
